import { html, render } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import { ref } from 'lit-html/directives/ref.js';

import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { uniqueId } from '@disy/lib/unique-id';
import { setupPopupButton } from '@disy/ui/popup';

import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id.gen';
import {
  getActiveViewId,
  getAffectedViewAndDataViewByHierarchyRelations,
  getAffectedViewAndDataViewRelations,
  getConditionById,
  getViewById,
  isConditionDeactivatedForActiveWorksheet,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { AffectedViewsFlyout } from 'cadenza/workbook/workbook-panel/analysis-context/affected-views-flyout/affected-views-flyout';

import './affected-views-label.css';
import i18n from './affected-views-label.properties';

const COMPONENT_NAME = 'd-affected-views-label';

export class AffectedViewsLabel extends HTMLElement {
  readonly #conditionId?: ConditionId;
  readonly #hierarchyId?: HierarchyId;

  #removePopupButtonClickListener?: () => void;
  #tearDown: Teardown[] = [];

  constructor({ conditionId }: { conditionId: ConditionId });
  constructor({ hierarchyId }: { hierarchyId: HierarchyId });
  constructor({ conditionId, hierarchyId }: { conditionId?: ConditionId; hierarchyId?: HierarchyId }) {
    super();
    this.#conditionId = conditionId;
    this.#hierarchyId = hierarchyId;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#tearDown = [
      observeStore(
        getStore(),
        (state) => [
          getActiveViewId(state),
          this.#conditionId ?
            getAffectedViewAndDataViewRelations(state, this.#conditionId)
          : getAffectedViewAndDataViewByHierarchyRelations(state, this.#hierarchyId!),
        ],
        () => this.#render(),
        true,
      ),
    ];
  }

  disconnectedCallback() {
    this.#tearDown = tearDown(this.#tearDown);
  }

  #render() {
    const conditionId = this.#conditionId;
    const deactivated =
      conditionId ? isConditionDeactivatedForActiveWorksheet(getStore().getState(), conditionId) : false;
    const { affectedViews, affectedViewsWithLocalConditions } =
      conditionId ? getAffectedViewsForCondition(conditionId) : getAffectedViewsForHierarchy(this.#hierarchyId!);
    const count = affectedViews.length;
    const activeViewId = getActiveViewId(getStore().getState());
    render(
      html`<button
        ?disabled=${deactivated}
        class=${classMap({
          'has-affected-views': count > 0,
          'affects-active-view': affectedViews.some((view) => view.id === activeViewId),
        })}
        ${ref((button) =>
          this.#setupPopup(button as HTMLButtonElement | undefined, affectedViews, affectedViewsWithLocalConditions),
        )}
      >
        <span aria-hidden="true">${count}</span>
        <span ?hidden=${deactivated} class="visuallyhidden" id="${uniqueId()}"
          >${i18n('affected-views.label', { count })}</span
        >
      </button>`,
      this,
    );
  }

  #setupPopup(
    button: HTMLButtonElement | undefined,
    affectedViews: ViewDto[],
    affectedViewsWithLocalConditions: ViewDto[],
  ) {
    this.#removePopupButtonClickListener = tearDown(this.#removePopupButtonClickListener);
    if (button) {
      this.#removePopupButtonClickListener = setupPopupButton(
        button!,
        (_, popup) => {
          popup.tabIndex = -1;
          setTimeout(() => popup.focus());
          return new AffectedViewsFlyout(affectedViews, affectedViewsWithLocalConditions, this.#conditionId);
        },
        {
          arrow: true,
          styleClass: 'd-affected-views-flyout',
          placement: 'right-start',
        },
      );
    }
  }
}

customElements.define(COMPONENT_NAME, AffectedViewsLabel);

function getAffectedViewsForCondition(conditionId: ConditionId) {
  const relations = getAffectedViewAndDataViewRelations(getStore().getState(), conditionId);
  return {
    affectedViews: computeViewWithAffectedDataViews(relations.affectedViews, relations.affectedDataViews),
    affectedViewsWithLocalConditions: computeViewWithAffectedDataViewsLocallyOverridden(
      relations.affectedViewsWithLocalConditions,
      relations.affectedDataViewsWithLocalConditions,
      conditionId,
    ),
  };
}

function getAffectedViewsForHierarchy(hierarchyId: HierarchyId) {
  const relations = getAffectedViewAndDataViewByHierarchyRelations(getStore().getState(), hierarchyId);
  return {
    affectedViews: computeViewWithAffectedDataViews(relations.affectedViews, relations.affectedDataViews),
    affectedViewsWithLocalConditions: [],
  };
}

function computeViewWithAffectedDataViews(affectedViews: ViewId[], affectedDataViews: DataViewId[]) {
  const affectedDataViewsSet = new Set(affectedDataViews);
  return affectedViews
    .map((viewId) => getViewById(getStore().getState(), viewId))
    .map((view) => {
      return {
        ...view,
        dataViews: view.dataViews.filter((dataViewId) => affectedDataViewsSet.has(dataViewId)),
      };
    });
}

// Spatial conditions actually can't be overridden locally, however they are still present at the
// view level because they can apply to a different attribute for each view.
function computeViewWithAffectedDataViewsLocallyOverridden(
  affectedViewsWithLocalConditions: ViewId[],
  affectedDataViewsWithLocalConditions: DataViewId[],
  conditionId: ConditionId,
) {
  const condition = getConditionById(getStore().getState(), conditionId);
  return condition && condition.type === 'PolygonCondition' ?
      []
    : computeViewWithAffectedDataViews(affectedViewsWithLocalConditions, affectedDataViewsWithLocalConditions);
}
